import React, { useState } from "react";
import {
  Button,
  FormFieldSet,
  InfoText,
  Modal,
  StatusTags,
  TableAdv,
  DocumentsList,
} from "../../../../../components";
import { mapTableRows } from "../../../../../utils";
import { uploadFiles } from "../../../../../redux/slices/universal/fileUploadApi";
import { showToast } from "../../../../../components";
import { v4 as uuidv4 } from "uuid";

import { dateFormator } from "../../../../../utils";
import { useLoginUser } from "../../../../../hooks/useLoginUser";

const formatDisplayDate = (value) => {
  if (!value || value === "-" || value === "Invalid Date") return "-";
  if (typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value)) return value;
  return dateFormator(value, "date-month-year");
};

const InfoTextUse = ({ title, value }) => {
  return <InfoText size="sm" textWrap={true} title={title} value={value} />;
};

const DocumentTable = ({ data, onUpdate, candidateId, usersMap = {} }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [addDocument, setAddDocument] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ documentType: "", file: null });
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const { loginUserUiid, userData } = useLoginUser();
  const currentUserName = `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim() || loginUserUiid;

  const rows = data || [];

  const addNewDocument = () => {
    setIsModalOpen(true);
    setAddDocument(true);
  };

  const editDocument = (row) => {
    setSelectedRow(row);
    setIsModalOpen(true);
    setIsEditMode(true);
  };
  const viewDocument = (row) => {
    setSelectedRow(row);
    setSelectedDocuments([{ 
      name: row.name || row.documentType, 
      filePath: row.key || row.url,
      isWorkforce: true
    }]);
    setIsModalOpen(true);
    setIsViewMode(true);
  };

  const cancelAction = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setIsViewMode(false);
    setAddDocument(false);
    setFormData({ documentType: "", file: null });
    setSelectedDocuments([]);
  };

  const handleSaveDocument = async () => {
    if (!formData.file || !formData.documentType) {
      showToast("Please select a document type and file", "error");
      return;
    }

    try {
      setUploading(true);
      const res = await uploadFiles({
        coll: "Document",
        entity: "incomingplacements",
        category: "IncomingPlacementDocuments",
        uiid: candidateId || "0",
        doc: formData.file,
        uploadType: "single",
        field: "doc",
        workforce_upload:true
      });

      if (res?.success) {
        const uploadedData = res?.data?.data;

        const fileKey = uploadedData?.key || "";
        const fileUrl = uploadedData?.url || "";

        const newDoc = {
          id: uuidv4(),
          documentType: formData.documentType,
          uploadedOn: dateFormator(new Date(), "date-month-year"),
          uploadedBy: loginUserUiid,
          key: fileKey,     // ✅ store key
          url: fileUrl,     // ✅ store url
          name: formData.file.name
        };
        if (onUpdate) onUpdate([...rows, newDoc]);
        showToast("Document uploaded successfully", "success");
        cancelAction();
      } else {
        showToast("Upload failed", "error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      showToast("Upload error", "error");
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      key: "documentType",
      label: "Document Type",
    },
    {
      key: "uploadedOn",
      label: "Uploaded On",
      render: (row) => formatDisplayDate(row.uploadedOn)
    },
    {
      key: "uploadedBy",
      label: "Uploaded By",
      render: (row) => {
        if (row.uploadedBy === loginUserUiid) return currentUserName;
        return usersMap[row.uploadedBy]?.name || row.uploadedBy || "-";
      }
    },
    {
      key: "",
      label: "Action",
      render: (row) => {
        return (
          <div className="flex items-center gap-2">
            <Button
              type="icon"
              variant="text"
              color="dark"
              icon={"edit_square"}
              size="xs"
              onClick={() => editDocument(row)}
            />
            <Button
              type="icon"
              variant="text"
              color="dark"
              icon={"visibility"}
              size="xs"
              onClick={() => viewDocument(row)}
            />
          </div>
        );
      },
    },
  ];
  const mappedRows = mapTableRows(rows, columns);
  return (
    <div className="flex-1 flex flex-col  overflow-hidden">
      <TableAdv
        columns={columns}
        rows={mappedRows}
        limit={5}
        selectList={[5, 10]}
        toolbarRight={{
          search: true,
          add: [
            {
              component: (
                <div>
                  <Button
                    icon={"add"}
                    title="Add Document"
                    size="sm"
                    onClick={() => addNewDocument()}
                  />
                </div>
              ),
            },
          ],
        }}
      />
      <Modal
        title={isViewMode ? "View Document" : "Add Documents"}
        isOpen={isModalOpen}
        width={isViewMode ? "max-w-6xl" : "max-w-[900px]"}
        onClose={cancelAction}
      >
        <div className="flex flex-col gap-4 w-full p-5">
          {isViewMode && (
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                <InfoTextUse
                  title="Document Type"
                  value={selectedRow.documentType}
                />
                <InfoTextUse title="Uploaded On" value={formatDisplayDate(selectedRow.uploadedOn)} />
                <InfoTextUse title="Uploaded By" value={
                  selectedRow.uploadedBy === loginUserUiid 
                    ? currentUserName 
                    : (usersMap[selectedRow.uploadedBy]?.name || selectedRow.uploadedBy || "-")
                } />
              </div>
              
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm min-h-[400px]">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="font-semibold text-gray-700 text-sm">Document Preview</h3>
                </div>
                <div className="p-4">
                  <DocumentsList data={selectedDocuments} useAtsBackend={true} />
                </div>
              </div>
            </div>
          )}
          {isEditMode && <div>isEditMode</div>}
          {addDocument && (
            <>
              <div className="flex-1 flex gap-4 w-full">
                <div className="flex-1">
                  <FormFieldSet
                    type="select"
                    title="Document Type"
                    options={[
                      { label: "Direct Hire Agreement", value: "Direct Hire Agreement" },
                      { label: "Offer Letter", value: "Offer Letter" },
                      { label: "Other", value: "Other" },
                    ]}
                    value={formData.documentType}
                    onChange={(val) => setFormData(prev => ({ ...prev, documentType: val }))}
                  />
                </div>
                <div className="flex-1">
                  <FormFieldSet
                    type="file"
                    title="Upload Document"
                    onChange={(files) => setFormData(prev => ({ ...prev, file: files[0] }))}
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button
                  title="Cancel"
                  variant="outlined"
                  onClick={cancelAction}
                />
                <Button
                  title={uploading ? "Uploading..." : "Save"}
                  onClick={handleSaveDocument}
                  disabled={uploading}
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DocumentTable;
